// useTreeLayout — turn the flat `IOrgGraphResponse` node list into a
// d3-hierarchy `HierarchyPointNode` tree with computed (x, y) coordinates,
// using the classic Reingold-Tilford algorithm via `tree()`.
//
// Pure layout: no React state, no side effects. Returned each render. The
// caller is responsible for memoising the input data if it wants to skip
// recomputation (the call cost is O(N log N) — acceptable for org sizes up
// to a few thousand).
//
// Behaviour notes:
//   - Orphans (`managerUpn === null` AND no direct reports) are dropped BEFORE
//     stratification. An orphan is not meaningful inside a hierarchy view and
//     would force d3 to either error (multiple roots) or attach disconnected
//     nodes to a virtual root, neither of which renders sensibly.
//   - If two or more REAL roots remain after orphan filtering, a synthetic
//     root id (`__virtual_root__`) is injected so `stratify()` succeeds. The
//     caller (`OrgTree.tsx`) recognises this id and skips rendering its card.
//   - `collapsedIds` is plumbed through for US2: when a node id is collapsed
//     its descendants are not laid out (we strip them from the input data
//     before stratification). US1 always passes an empty set.

import { useMemo } from 'react';
import {
  stratify,
  tree,
  type HierarchyPointNode,
} from 'd3-hierarchy';
import type { IOrgGraphNode, IOrgGraphResponse } from '@skystone/shared';
import { augmentWithGhosts } from '../../org-graph/ghosts';

// Ghost-manager synthesis is shared with the Org Graph view; re-exported here
// so existing tree imports (`OrgTree.tsx`) keep their import site.
export { GHOST_ID_PREFIX, isGhostId } from '../../org-graph/ghosts';

/** Width (logical SVG units) of one node card. */
export const NODE_WIDTH = 180;
/** Height (logical SVG units) of one node card. */
export const NODE_HEIGHT = 60;
/** Horizontal whitespace between sibling cards. */
export const HORIZONTAL_GAP = 30;
/** Vertical whitespace between a parent row and its child row. */
export const VERTICAL_GAP = 90;

/** Id used for the synthetic root when more than one real root remains. */
export const VIRTUAL_ROOT_ID = '__virtual_root__';

/**
 * Sentinel "virtual root" node carrying just enough shape to satisfy
 * `IOrgGraphNode`. Used only when we need to fuse multiple real roots into a
 * single tree so `stratify()` doesn't throw. The renderer suppresses its
 * card via the `VIRTUAL_ROOT_ID` check.
 */
const VIRTUAL_ROOT_NODE: IOrgGraphNode = {
  id: VIRTUAL_ROOT_ID,
  displayName: '',
  jobTitle: null,
  departmentId: 'unassigned',
  mail: null,
  managerUpn: null,
  unresolvedManagerName: null,
  isCurrentUser: false,
  isExternal: false,
};

/**
 * Result returned by `useTreeLayout`. `root` is `null` when the input data
 * has no renderable nodes (all-orphans, or every node collapsed away).
 */
export interface TreeLayoutResult {
  root: HierarchyPointNode<IOrgGraphNode> | null;
  /** True when a `VIRTUAL_ROOT_ID` node was injected at the top. */
  hasVirtualRoot: boolean;
}

/**
 * Pre-compute "this id has at least one direct report" from the node list's
 * own `managerUpn` field. Exported so callers (e.g. `OrgTree.tsx`'s team-
 * colour assignment) derive the same set from a single source of truth —
 * deriving it independently from `data.edges` would silently disagree if
 * edges and `managerUpn` ever diverged via a data-integrity issue in the
 * sync (sonar-review feedback on PRODOPSAI-467).
 */
export function computeHasReports(nodes: readonly IOrgGraphNode[]): Set<string> {
  const set = new Set<string>();
  for (const n of nodes) {
    if (n.managerUpn !== null) set.add(n.managerUpn);
  }
  return set;
}

/**
 * Walks the supplied data and returns a new `nodes` array that:
 *   1. Excludes orphans (no manager AND no reports).
 *   2. Excludes descendants of any id in `collapsedIds`.
 *   3. Re-parents nodes whose manager is no longer in the visible set to
 *      `null` so they survive as additional roots.
 *
 * Pure helper — no React.
 */
function visibleNodes(
  data: IOrgGraphResponse,
  collapsedIds: ReadonlySet<string>,
): IOrgGraphNode[] {
  // Pre-compute "this id has at least one direct report" so we can identify
  // orphans (no manager AND no reports). Shared with `OrgTree.tsx`'s
  // team-colour assignment via `computeHasReports`, so both code paths
  // derive the same set from a single source of truth (the node list's
  // `managerUpn` field).
  const hasReports = computeHasReports(data.nodes);

  // Drop orphans first so they never enter the tree.
  const nonOrphans = data.nodes.filter(
    (n) => n.managerUpn !== null || hasReports.has(n.id),
  );

  // Drop descendants of any collapsed node. We walk down from each collapsed
  // ancestor BFS-style and collect every transitive child for removal.
  const removed = new Set<string>();
  if (collapsedIds.size > 0) {
    const childrenByManager = new Map<string, string[]>();
    for (const n of nonOrphans) {
      if (n.managerUpn === null) continue;
      const list = childrenByManager.get(n.managerUpn) ?? [];
      list.push(n.id);
      childrenByManager.set(n.managerUpn, list);
    }
    const queue: string[] = [];
    for (const id of collapsedIds) {
      // The collapsed node itself stays visible — only its descendants hide.
      const direct = childrenByManager.get(id) ?? [];
      queue.push(...direct);
    }
    while (queue.length > 0) {
      const id = queue.shift() as string;
      if (removed.has(id)) continue;
      removed.add(id);
      const direct = childrenByManager.get(id) ?? [];
      queue.push(...direct);
    }
  }

  const surviving = nonOrphans.filter((n) => !removed.has(n.id));

  // Re-parent any node whose manager is no longer in the surviving set
  // (e.g. its manager was an orphan we dropped). They become additional
  // roots, which we'll fuse with a virtual root below.
  const survivingIds = new Set(surviving.map((n) => n.id));
  return surviving.map((n) =>
    n.managerUpn !== null && !survivingIds.has(n.managerUpn)
      ? { ...n, managerUpn: null }
      : n,
  );
}

/**
 * If more than one root remains after orphan filtering, fuse them under a
 * synthetic virtual root. Returns the (possibly-augmented) node list and a
 * flag the caller can use to suppress the virtual root from rendering.
 */
function injectVirtualRootIfNeeded(nodes: IOrgGraphNode[]): {
  nodes: IOrgGraphNode[];
  hasVirtualRoot: boolean;
} {
  const roots = nodes.filter((n) => n.managerUpn === null);
  if (roots.length <= 1) {
    return { nodes, hasVirtualRoot: false };
  }
  const reparented = nodes.map((n) =>
    n.managerUpn === null ? { ...n, managerUpn: VIRTUAL_ROOT_ID } : n,
  );
  return {
    nodes: [VIRTUAL_ROOT_NODE, ...reparented],
    hasVirtualRoot: true,
  };
}

/**
 * Build the laid-out tree.
 *
 * @param data - the org-graph payload (we only use `nodes`).
 * @param collapsedIds - ids whose descendants should not be laid out. Defaults
 *   to an empty set (everything fully expanded) — US1 always passes this.
 */
export function useTreeLayout(
  data: IOrgGraphResponse,
  collapsedIds: ReadonlySet<string> = new Set(),
): TreeLayoutResult {
  return useMemo(() => {
    // Inject ghost-manager bridge nodes BEFORE orphan filtering so people
    // whose only link is an outside-export manager survive (their ghost is
    // now their manager). Ghosts themselves have reports, so they survive
    // the orphan filter too. The tree ignores the derived edges.
    const withGhosts = augmentWithGhosts(data.nodes).nodes;
    const visible = visibleNodes({ ...data, nodes: withGhosts }, collapsedIds);
    if (visible.length === 0) {
      return { root: null, hasVirtualRoot: false };
    }

    const { nodes: prepared, hasVirtualRoot } = injectVirtualRootIfNeeded(visible);

    const stratifier = stratify<IOrgGraphNode>()
      .id((d) => d.id)
      .parentId((d) => d.managerUpn);

    let rooted;
    try {
      rooted = stratifier(prepared);
    } catch {
      // Defensive: a cycle in the data (manager A -> B -> A) would throw.
      // We surface no tree rather than crashing the page; the caller
      // renders an empty/error state.
      return { root: null, hasVirtualRoot: false };
    }

    const layout = tree<IOrgGraphNode>().nodeSize([
      NODE_WIDTH + HORIZONTAL_GAP,
      NODE_HEIGHT + VERTICAL_GAP,
    ]);

    return { root: layout(rooted), hasVirtualRoot };
  }, [data, collapsedIds]);
}
